import type { Channel, StudioId } from '@/domain/types';
import { matchCustomer, createCustomer } from '@/domain/selfBookFlow';
import { normalizeInstagramHandle, isValidInstagramHandle } from '@/domain/instagram';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { persistRecord } from '@/api/persist';

/** Resolve (or create) the customer behind an inbound DM handle. Matches on the
 *  normalized handle (strong-ID); creates with source = the DM channel when new.
 *  A malformed handle is not a reliable identity key, so it is dropped: no match
 *  on it, and it is not stored (the record falls back to a plain name). */
export function resolveInboundDmCustomer(input: {
  handle: string;
  name?: string;
  studioId: StudioId;
  channel: Channel;
}): string {
  const normalized = normalizeInstagramHandle(input.handle);
  const handle = isValidInstagramHandle(normalized) ? normalized : '';
  const m = handle ? matchCustomer({ instagramHandle: handle }) : ({ kind: 'none', nameHints: [] } as const);
  if (m.kind === 'matched') return m.id;
  return createCustomer({
    name: input.name?.trim() || (handle ? `@${handle}` : 'Instagram DM'),
    instagramHandle: handle || undefined,
    studioId: input.studioId,
    sourceChannel: input.channel,
  });
}

/** Demo/console seam: seed an inbound IG DM from a (possibly new) handle —
 *  create-or-match the customer, then append an inbound ig-dm message so the
 *  thread surfaces in the Messenger inbox. Returns the resolved customer id. */
export function seedInboundDm(input: {
  handle?: string;
  name?: string;
  studioId: StudioId;
  body?: string;
}): string {
  const nowIso = useSimulationStore.getState().clockIso;
  const handle = normalizeInstagramHandle(input.handle || 'ink_curious');
  // Snapshot the ids BEFORE resolve so we can tell a brand-new customer (needs a
  // DB commit) from a matched one (already in the DB — re-PUTting our store copy
  // could clobber server state, the tv-k9b.14 class).
  const knownIds = new Set(Object.keys(useBrainStore.getState().customers));
  const customerId = resolveInboundDmCustomer({
    handle,
    name: input.name,
    studioId: input.studioId,
    channel: 'ig-dm',
  });
  useBrainStore.getState().addConversationEntry(customerId, {
    id: `convo-inbound-dm-${crypto.randomUUID().slice(0, 8)}`,
    customerId,
    channel: 'ig-dm',
    direction: 'in',
    body: input.body?.trim() || 'Hey! Do you have any availability this month?',
    timestamp: nowIso,
    authorRole: 'customer',
  });
  // A customer who messages must become a first-class DB record right away, with
  // their IG handle and this first message — not left to the debounced write-
  // through bridge, which a quick "send deposit link" outruns (the atomic hold-
  // slot booking insert then 500s on the NOT-NULL customer FK → no ghost, tv-k9b).
  // Commit only a genuinely NEW customer (a matched one is already persisted).
  if (!isStoreFallbackEnabled() && !knownIds.has(customerId)) {
    const record = useBrainStore.getState().customers[customerId];
    if (record) void persistRecord('customers', customerId, record);
  }
  return customerId;
}
